import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  CubeIcon,
  CodeBracketIcon,
  BookOpenIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { integrationService, ParsedEndpoint, SpecSummary } from '../services/integrationService';
import { Button } from '../../../components';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

type Step = 'type' | 'native_config' | 'openapi_upload' | 'openapi_review' | 'done';

const INTEGRATION_TYPES = [
  {
    type: 'native',
    label: 'Native Connector',
    description: 'Pre-built integration with popular platforms',
    icon: CubeIcon,
    platforms: ['shopify', 'clerk'],
  },
  {
    type: 'openapi',
    label: 'OpenAPI / Swagger',
    description: 'Connect any API by uploading its OpenAPI spec',
    icon: CodeBracketIcon,
  },
  {
    type: 'knowledge_only',
    label: 'Knowledge Only',
    description: 'No platform integration — AI answers from knowledge base only',
    icon: BookOpenIcon,
  },
];

export default function AddIntegrationModal({ open, onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('type');
  const [selectedType, setSelectedType] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('shopify');
  const [baseUrl, setBaseUrl] = useState('');
  const [authType, setAuthType] = useState('bearer');
  const [authToken, setAuthToken] = useState('');

  // OpenAPI state
  const [specInput, setSpecInput] = useState<'text' | 'url'>('url');
  const [specUrl, setSpecUrl] = useState('');
  const [specText, setSpecText] = useState('');
  const [parsedSummary, setParsedSummary] = useState<SpecSummary | null>(null);
  const [parsedEndpoints, setParsedEndpoints] = useState<ParsedEndpoint[]>([]);
  const [enabledEndpoints, setEnabledEndpoints] = useState<Set<number>>(new Set());
  // const [createdIntegrationId, setCreatedIntegrationId] = useState<string | null>(null);

  const parseMutation = useMutation({
    mutationFn: () =>
      integrationService.parseSpec(
        specInput === 'url' ? { spec_url: specUrl } : { spec_text: specText }
      ),
    onSuccess: (data) => {
      setParsedSummary(data.summary);
      setParsedEndpoints(data.endpoints);
      // Enable all GET endpoints by default
      const defaults = new Set<number>();
      data.endpoints.forEach((ep, i) => {
        if (ep.method === 'GET') defaults.add(i);
      });
      setEnabledEndpoints(defaults);
      setStep('openapi_review');
      if (data.summary.baseUrl && !baseUrl) {
        setBaseUrl(data.summary.baseUrl);
      }
      if (data.summary.title && !name) {
        setName(data.summary.title);
      }
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to parse spec');
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      // Build auth config
      const authConfig: Record<string, string> = {};
      if (selectedType === 'native' && platform === 'clerk') {
        // Clerk uses a long-lived API key sent as Authorization: Bearer.
        // We label it "API Key" in the UI to match Clerk's terminology.
        if (authToken) authConfig.api_key = authToken;
      } else if (authType === 'bearer') {
        authConfig.token = authToken;
      } else if (authType === 'api_key') {
        authConfig.api_key = authToken;
        authConfig.header_name = 'X-API-Key';
        authConfig.location = 'header';
      }

      // Create the integration
      const integration = await integrationService.create({
        name,
        type: selectedType,
        platform: selectedType === 'native' ? platform : undefined,
        base_url: baseUrl || undefined,
        auth_type:
          selectedType === 'native' && platform === 'clerk'
            ? 'bearer'
            : authType || undefined,
        auth_config: Object.keys(authConfig).length > 0 ? authConfig : undefined,
      });

      // For OpenAPI: save selected endpoints
      if (selectedType === 'openapi' && parsedEndpoints.length > 0) {
        const endpointsToSave = parsedEndpoints
          .filter((_, i) => enabledEndpoints.has(i))
          .map((ep) => ({
            path: ep.path,
            method: ep.method,
            operationId: ep.operationId,
            toolName: ep.suggestedToolName,
            toolDescription: ep.suggestedDescription,
            permissionLevel: ep.suggestedPermission,
            requiresApproval: ep.suggestedPermission === 'write',
            enabled: true,
            parameters: ep.parameters,
            requestBodySchema: ep.requestBody,
            responseSchema: ep.responseSchema,
          }));

        if (endpointsToSave.length > 0) {
          await integrationService.saveEndpoints(integration.id, endpointsToSave);
        }
      }

      return integration;
    },
    onSuccess: () => {
      toast.success('Integration created');
      onCreated();
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to create integration');
    },
  });

  const toggleEndpoint = (index: number) => {
    const next = new Set(enabledEndpoints);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setEnabledEndpoints(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-panel border border-line bg-surface shadow-l">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h3 className="font-display text-lg font-semibold text-ink">
            {step === 'type' && 'Add Integration'}
            {step === 'native_config' && 'Configure Native Connector'}
            {step === 'openapi_upload' && 'Upload API Specification'}
            {step === 'openapi_review' && 'Review Endpoints'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-ink-2 hover:bg-black/[0.04]">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          {/* Step 1: Choose type */}
          {step === 'type' && (
            <div className="space-y-3">
              {INTEGRATION_TYPES.map((t) => (
                <button
                  key={t.type}
                  onClick={() => {
                    setSelectedType(t.type);
                    if (t.type === 'native') setStep('native_config');
                    else if (t.type === 'openapi') setStep('openapi_upload');
                    else {
                      setSelectedType('knowledge_only');
                      setName('Knowledge Only');
                    }
                  }}
                  className="flex w-full items-center gap-4 rounded-card border border-line p-4 text-left transition hover:border-brand/30 hover:bg-brand/5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-2">
                    <t.icon className="h-5 w-5 text-ink-2" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-ink">{t.label}</h4>
                    <p className="mt-0.5 text-sm text-ink-3">{t.description}</p>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-ink-3" />
                </button>
              ))}
            </div>
          )}

          {/* Step 2a: Native config */}
          {step === 'native_config' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-2">Integration Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={platform === 'clerk' ? 'e.g., Clerk Accounting' : 'e.g., My Shopify Store'}
                  className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-2">Platform</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="shopify">Shopify</option>
                  <option value="clerk">Clerk (Accounting / PDF Invoices)</option>
                </select>
              </div>

              {platform === 'shopify' && (
                <p className="text-xs text-ink-3">
                  Native connectors use the existing platform settings. Configure credentials in the Shopify tab.
                </p>
              )}

              {platform === 'clerk' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-ink-2">Clerk API Key</label>
                    <input
                      type="password"
                      value={authToken}
                      onChange={(e) => setAuthToken(e.target.value)}
                      placeholder="clk_..."
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    <p className="mt-1 text-xs text-ink-3">
                      Create one in Clerk under <span className="font-mono">Settings &rarr; API Keys</span>. The key is shown only once.
                      Use a key whose user has <span className="font-mono">accountant</span> role or higher so the agent can create invoices.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-2">Base URL <span className="text-ink-3">(optional)</span></label>
                    <input
                      type="url"
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      placeholder="https://clerk-api.faxter.com"
                      className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2b: OpenAPI upload */}
          {step === 'openapi_upload' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-2">Integration Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Clinic Booking API"
                  className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              {/* Spec input method toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSpecInput('url')}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${
                    specInput === 'url' ? 'bg-brand/10 text-brand' : 'text-ink-2 hover:bg-black/[0.04]'
                  }`}
                >
                  <LinkIcon className="h-4 w-4" />
                  URL
                </button>
                <button
                  onClick={() => setSpecInput('text')}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${
                    specInput === 'text' ? 'bg-brand/10 text-brand' : 'text-ink-2 hover:bg-black/[0.04]'
                  }`}
                >
                  <DocumentArrowUpIcon className="h-4 w-4" />
                  Paste
                </button>
              </div>

              {specInput === 'url' ? (
                <div>
                  <label className="block text-sm font-medium text-ink-2">OpenAPI Spec URL</label>
                  <input
                    type="url"
                    value={specUrl}
                    onChange={(e) => setSpecUrl(e.target.value)}
                    placeholder="https://api.example.com/openapi.json"
                    className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-ink-2">Paste Spec (JSON or YAML)</label>
                  <textarea
                    value={specText}
                    onChange={(e) => setSpecText(e.target.value)}
                    placeholder='{"openapi": "3.0.0", ...}'
                    rows={8}
                    className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-ink-2">Base URL</label>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.example.com/v1"
                  className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
                <p className="mt-1 text-xs text-ink-3">Can be auto-detected from spec</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-2">Auth Type</label>
                  <select
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                    className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="bearer">Bearer Token</option>
                    <option value="api_key">API Key</option>
                    <option value="basic">Basic Auth</option>
                    <option value="">None</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-2">
                    {authType === 'api_key' ? 'API Key' : authType === 'basic' ? 'Username:Password' : 'Token'}
                  </label>
                  <input
                    type="password"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="Enter credentials"
                    className="mt-1 block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review endpoints */}
          {step === 'openapi_review' && parsedSummary && (
            <div className="space-y-4">
              <div className="rounded-card bg-surface-2 p-3">
                <p className="text-sm font-medium text-ink">{parsedSummary.title || 'API Spec'}</p>
                <p className="mt-1 text-xs text-ink-2">
                  {parsedSummary.endpointCount} endpoints found
                  {parsedSummary.tags.length > 0 && ` across ${parsedSummary.tags.join(', ')}`}
                </p>
              </div>

              <p className="text-sm text-ink-2">
                Select which endpoints the AI agent should be able to use. GET endpoints are enabled by default.
              </p>

              <div className="space-y-2">
                {parsedEndpoints.map((ep, i) => (
                  <label
                    key={`${ep.method}-${ep.path}`}
                    className="flex items-center gap-3 rounded-card border border-line p-3 transition hover:border-line-2"
                  >
                    <input
                      type="checkbox"
                      checked={enabledEndpoints.has(i)}
                      onChange={() => toggleEndpoint(i)}
                      className="h-4 w-4 rounded border-line bg-surface text-brand focus:ring-brand/20"
                    />
                    <span className={`w-16 rounded px-1.5 py-0.5 text-center text-xs font-mono font-bold ${
                      ep.method === 'GET' ? 'bg-brand/10 text-brand' :
                      ep.method === 'POST' ? 'bg-sky/15 text-sky' :
                      ep.method === 'PUT' || ep.method === 'PATCH' ? 'bg-warning/10 text-warning' :
                      'bg-danger/10 text-danger'
                    }`}>
                      {ep.method}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm text-ink">{ep.suggestedToolName}</p>
                      <p className="truncate text-xs text-ink-3">{ep.path}</p>
                    </div>
                    <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-ink-2">
                      {ep.suggestedPermission}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-line px-6 py-4">
          <div>
            {step !== 'type' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (step === 'native_config' || step === 'openapi_upload') setStep('type');
                  else if (step === 'openapi_review') setStep('openapi_upload');
                }}
              >
                <ArrowLeftIcon className="mr-1.5 h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            {step === 'native_config' && (
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={
                  !name ||
                  createMutation.isPending ||
                  (platform === 'clerk' && !authToken)
                }
              >
                {createMutation.isPending ? 'Creating...' : 'Create Integration'}
              </Button>
            )}
            {step === 'openapi_upload' && (
              <Button
                size="sm"
                onClick={() => parseMutation.mutate()}
                disabled={(!specUrl && !specText) || parseMutation.isPending}
              >
                {parseMutation.isPending ? 'Parsing...' : 'Parse Spec'}
              </Button>
            )}
            {step === 'openapi_review' && (
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={enabledEndpoints.size === 0 || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : `Create with ${enabledEndpoints.size} Endpoints`}
              </Button>
            )}
            {step === 'type' && selectedType === 'knowledge_only' && (
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
